// ── MERCH STORE: admin catalog and preview (Orders is admin-store-orders.js) ──
// REQUIRED_CATEGORY_LABELS is also read by the parent-facing Store view
// (parent-store.js's VIEWS['p-store']), and PREVIEW_PERFORMERS is also read
// by the Classes admin preview (admin-tapvoice.js's VIEWS['a-classes-preview'])
// — both are real `export`s below for those two cross-module reads, in
// addition to being used here. esc stays a bare identifier — it's a top-level
// `const` in /js/shared/esc.js's classic script, which (unlike an ES
// module's own top-level bindings) lives in the shared global lexical
// environment every script on the page can already see.
//
// Catalog buttons wired via VIEW_INIT['a-store-catalog'] + wireStoreCatalogGroup()
// (same wireXGroup shape as catalog-shared.js) — scSetFilter/scToggleExpand/
// toggleSaleActive/toggleSaleVisibility/toggleProductActive/saveSale/saveProduct
// all rewrite storeCatalogList/storeCatalogTabs directly (bypassing route()'s
// VIEW_INIT lifecycle) and call wireStoreCatalogGroup() afterward. saleModal's/
// productModal's primary Save buttons and productModal's size-chip controls are
// wired directly inline right after openModal(), same pattern as billing-shared.js's
// cardModal. Preview (VIEW_INIT['a-store-preview']) follows the same shape.
// previewSale is exported for the Orders screen's Preview button.
import { apiFetch } from '../core/api.js';
import { STORE_SALES, currentView } from '../core/state.js';
import { fmtLocalDT, readMoney, moneyValue } from '../core/format.js';
import { openModal, closeModal, toast, toastError, ICON_COPY, ICON_ARCHIVE, ICON_RESTORE } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh, route } from '../core/routing.js';

export const REQUIRED_CATEGORY_LABELS = { boy_shorts: 'Boy shorts', tights: 'Tights', leggings: 'Leggings' };
let scFilter = 'active'; // 'active' | 'archived'
let scExpandedSaleId = null;
let scDuplicateSourceProducts = null; // set by duplicateSale(), consumed by saveSale()

function scSetFilter(mode) {
  scFilter = mode;
  const listEl = document.getElementById('storeCatalogList');
  if (listEl) listEl.innerHTML = renderStoreCatalogList();
  const tabsEl = document.getElementById('storeCatalogTabs');
  if (tabsEl) tabsEl.outerHTML = renderStoreCatalogTabs();
  wireStoreCatalogGroup();
}
// Re-attaches listeners for the catalog tabs and list-row actions — called
// after every direct rewrite of storeCatalogTabs/storeCatalogList above, and
// from VIEW_INIT['a-store-catalog'] for the initial render.
function wireStoreCatalogGroup() {
  const tabsEl = document.getElementById('storeCatalogTabs');
  if (tabsEl) tabsEl.querySelectorAll('[data-action="sc-set-filter"]').forEach(btn => {
    btn.addEventListener('click', () => scSetFilter(btn.dataset.mode));
  });
  const listEl = document.getElementById('storeCatalogList');
  if (!listEl) return;
  listEl.querySelectorAll('[data-action="sc-toggle-expand"]').forEach(el => {
    el.addEventListener('click', () => scToggleExpand(el.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-preview-sale"]').forEach(btn => {
    btn.addEventListener('click', () => previewSale(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-toggle-visibility"]').forEach(btn => {
    btn.addEventListener('click', () => toggleSaleVisibility(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-edit-sale"]').forEach(btn => {
    btn.addEventListener('click', () => saleModal(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-duplicate-sale"]').forEach(btn => {
    btn.addEventListener('click', () => duplicateSale(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-toggle-sale-active"]').forEach(btn => {
    btn.addEventListener('click', () => toggleSaleActive(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-add-product"]').forEach(btn => {
    btn.addEventListener('click', () => productModal(btn.dataset.sale));
  });
  listEl.querySelectorAll('[data-action="sc-edit-product"]').forEach(btn => {
    btn.addEventListener('click', () => productModal(btn.dataset.sale, btn.dataset.product));
  });
  listEl.querySelectorAll('[data-action="sc-toggle-product-active"]').forEach(btn => {
    btn.addEventListener('click', () => toggleProductActive(btn.dataset.sale, btn.dataset.product));
  });
}
function renderStoreCatalogTabs() {
  const activeCount = STORE_SALES.filter(s => s.active !== false).length;
  const archivedCount = STORE_SALES.filter(s => s.active === false).length;
  const tab = (mode, label, count) => `<button data-action="sc-set-filter" data-mode="${mode}" style="padding:7px 14px;border-radius:7px;border:1px solid ${scFilter === mode ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${scFilter === mode ? 'rgba(201,168,76,0.08)' : 'transparent'};color:${scFilter === mode ? 'var(--gold-light)' : 'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">${label} (${count})</button>`;
  return `<div id="storeCatalogTabs" style="display:flex;gap:6px;margin-bottom:14px">${tab('active', 'Active', activeCount)}${tab('archived', 'Archived', archivedCount)}</div>`;
}
function scToggleExpand(saleId) {
  scExpandedSaleId = scExpandedSaleId === saleId ? null : saleId;
  const listEl = document.getElementById('storeCatalogList');
  if (listEl) listEl.innerHTML = renderStoreCatalogList();
  wireStoreCatalogGroup();
}
function renderStoreCatalogList() {
  const list = STORE_SALES
    .filter(s => scFilter === 'active' ? s.active !== false : s.active === false)
    .sort((a, b) => new Date(b.deadlineAt) - new Date(a.deadlineAt));
  if (!list.length) return `<p style="font-size:13px;color:var(--muted);font-style:italic;padding:8px 0">${scFilter === 'active' ? 'No active sales — add one to get started.' : 'No archived sales.'}</p>`;
  return list.map((s, i) => `
    <div style="${i < list.length - 1 ? 'border-bottom:1px solid var(--border-soft)' : ''};padding:10px 0">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px">
        <div style="cursor:pointer;flex:1" data-action="sc-toggle-expand" data-sale="${s.id}">
          <div style="font-size:14px;color:${s.active === false ? 'var(--muted)' : 'var(--cream)'}">${esc(s.name)}${s.visibleToParents === false ? ' <span class="pill pill-amber">Draft</span>' : ''}</div>
          <div style="font-size:12px;color:var(--muted);margin-top:2px">Season ${s.seasonYear} · Deadline ${fmtLocalDT(s.deadlineAt)} · ${s.products.length} ${s.products.length === 1 ? 'product' : 'products'}</div>
        </div>
        <div style="display:flex;gap:8px;flex-shrink:0">
          <button class="btn-ghost btn-sm" data-action="sc-preview-sale" data-sale="${s.id}">Preview</button>
          <button class="btn-ghost btn-sm" data-action="sc-toggle-visibility" data-sale="${s.id}">${s.visibleToParents === false ? 'Publish' : 'Unpublish'}</button>
          <button class="btn-ghost btn-sm" data-action="sc-edit-sale" data-sale="${s.id}">Edit</button>
          <button class="icon-btn" title="Duplicate into a new sale" aria-label="Duplicate into a new sale" data-action="sc-duplicate-sale" data-sale="${s.id}">${ICON_COPY}</button>
          <button class="icon-btn" title="${s.active === false ? 'Restore to active' : 'Archive'}" aria-label="${s.active === false ? 'Restore to active' : 'Archive'}" data-action="sc-toggle-sale-active" data-sale="${s.id}">${s.active === false ? ICON_RESTORE : ICON_ARCHIVE}</button>
        </div>
      </div>
      ${(scExpandedSaleId === s.id || list.length === 1) ? renderStoreProductList(s) : ''}
    </div>`).join('');
}
function renderStoreProductList(sale) {
  const products = [...sale.products].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  return `
    <div style="margin:10px 0 0 0;padding:12px;background:rgba(255,255,255,0.02);border-radius:8px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.1em;color:var(--muted)">Products</div>
        <button class="btn-ghost btn-sm" data-action="sc-add-product" data-sale="${sale.id}">+ Add product</button>
      </div>
      ${!products.length ? '<p style="font-size:13px;color:var(--muted);font-style:italic">No products yet.</p>' : products.map(p => `
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:6px 0">
          <div style="display:flex;align-items:center;gap:10px">
            ${p.photoUrl ? `<img src="${esc(p.photoUrl)}" style="width:36px;height:36px;border-radius:6px;object-fit:cover;flex-shrink:0;background:rgba(255,255,255,0.03)" onerror="this.style.display='none'">` : ''}
            <div>
              <div style="font-size:13px;color:${p.active === false ? 'var(--muted)' : 'var(--cream)'}">${esc(p.name)}${p.requiredCategory ? ` <span class="pill pill-gold">${esc(REQUIRED_CATEGORY_LABELS[p.requiredCategory] || p.requiredCategory)}</span>` : ''}</div>
              <div style="font-size:11px;color:var(--muted);margin-top:1px">${fmt$(p.price)} · Sizes: ${p.sizes.map(esc).join(', ')}</div>
            </div>
          </div>
          <div style="display:flex;gap:8px;flex-shrink:0">
            <button class="btn-ghost btn-sm" data-action="sc-edit-product" data-sale="${sale.id}" data-product="${p.id}">Edit</button>
            <button class="icon-btn" title="${p.active === false ? 'Restore' : 'Retire'}" aria-label="${p.active === false ? 'Restore' : 'Retire'}" data-action="sc-toggle-product-active" data-sale="${sale.id}" data-product="${p.id}">${p.active === false ? ICON_RESTORE : ICON_ARCHIVE}</button>
          </div>
        </div>`).join('')}
    </div>`;
}
// Renders the same "add an item" / required-items layout a real family sees
// on the Store tab, using the sale's actual products/prices/sizes, so an
// admin can sanity-check copy and setup without needing a PC-eligible test
// account. A real page (not a modal) — parents see p-store as a full page
// with a two-column layout, and squeezing that into a 460px modal box made
// it read as cramped instead of showing true-to-life spacing.
//
// Fully interactive up through the confirmation modal — add to cart, remove,
// see the running total, mark a required item as already-owned — all using
// synthetic stand-in performers (PREVIEW_PERFORMERS) and preview-only state
// (previewCart/previewSiUnits/previewAttestedKeys) so nothing here ever
// touches a real family or calls the real checkout/attestation endpoints.
// The only thing that doesn't happen is an actual Stripe charge: clicking
// Confirm in the modal ends the preview instead of placing a real order.
// dob is only used by the Classes preview (age-eligibility via calcAge()) — the
// Store preview above never reads it, so adding it here is safe for both.
export const PREVIEW_PERFORMERS = [
  { id: 'preview-female', name: 'Sample Performer (Female)', gender: 'Female', dob: '2015-03-01' },
  { id: 'preview-male', name: 'Sample Performer (Male)', gender: 'Male', dob: '2012-03-01' },
];
let previewSaleId = null;
let previewCart = [];
let previewSiUnits = [];
let previewAttestedKeys = {}; // "performerId:category" -> true, simulated locally only

export function previewSale(saleId) {
  previewSaleId = saleId;
  previewCart = [];
  previewSiUnits = [];
  previewAttestedKeys = {};
  route('a-store-preview');
}
function previewSiCurrentProduct() {
  const sale = STORE_SALES.find(s => s.id === previewSaleId);
  const productId = document.getElementById('previewProduct') ? document.getElementById('previewProduct').value : null;
  return sale && sale.products.find(p => p.id === productId);
}
function previewUnitRowsHtml(product) {
  const sizes = (product && product.sizes) || [];
  return previewSiUnits.map((u, i) => `
    <div style="padding:10px;border:1px solid var(--border-soft);border-radius:8px;margin-bottom:8px">
      ${previewSiUnits.length > 1 ? `<div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:var(--muted);margin-bottom:8px">Item ${i + 1}</div>` : ''}
      <div class="field-row">
        <div class="field"><label>Size</label>
          <select data-action="preview-unit-field" data-index="${i}" data-field="size">
            ${sizes.map(s => `<option value="${esc(s)}" ${u.size === s ? 'selected' : ''}>${esc(s)}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>For</label>
          <select data-action="preview-unit-field" data-index="${i}" data-field="performerId">
            ${PREVIEW_PERFORMERS.map(k => `<option value="${k.id}" ${u.performerId === k.id ? 'selected' : ''}>${esc(k.name)}</option>`).join('')}
            <option value="" ${!u.performerId ? 'selected' : ''}>Someone else</option>
          </select>
        </div>
      </div>
      ${!u.performerId ? `<div class="field"><label>Name</label><input type="text" value="${esc(u.recipientName || '')}" placeholder="e.g. Grandma" data-action="preview-unit-field" data-index="${i}" data-field="recipientName"></div>` : ''}
    </div>`).join('');
}
// Called both after the initial full render (via wireStorePreview) and after
// every direct rewrite of #previewUnitRows below (previewProductChanged,
// previewQtyChanged, previewUnitFieldChanged) — those bypass VIEW_INIT, so
// the rows they just wrote need their own re-wire.
function wireStorePreviewUnitRows() {
  const container = document.getElementById('previewUnitRows');
  if (!container) return;
  container.querySelectorAll('[data-action="preview-unit-field"]').forEach(el => {
    el.addEventListener('change', () => previewUnitFieldChanged(parseInt(el.dataset.index), el.dataset.field, el.value));
  });
}
function previewRenderUnitRows() {
  const container = document.getElementById('previewUnitRows');
  if (container) container.innerHTML = previewUnitRowsHtml(previewSiCurrentProduct());
  wireStorePreviewUnitRows();
}
function previewProductChanged() {
  const product = previewSiCurrentProduct();
  const qtyInput = document.getElementById('previewQty');
  if (qtyInput) qtyInput.value = 1;
  previewSiUnits = [{ size: (product && product.sizes[0]) || '', performerId: PREVIEW_PERFORMERS[0].id, recipientName: '' }];
  previewRenderUnitRows();
  const photo = document.getElementById('previewPhoto');
  if (photo) {
    if (product && product.photoUrl) { photo.src = product.photoUrl; photo.style.display = ''; }
    else { photo.style.display = 'none'; }
  }
}
function previewPhotoZoom() {
  const product = previewSiCurrentProduct();
  if (!product || !product.photoUrl) return;
  openModal(`<img src="${esc(product.photoUrl)}" style="width:100%;border-radius:8px;display:block">
    <div style="text-align:center;font-size:13px;color:var(--muted);margin-top:10px">${esc(product.name)}</div>`);
}
function previewQtyChanged() {
  const qtyInput = document.getElementById('previewQty');
  // Same cap as the parent-facing picker and the server.
  const qty = Math.min(20, Math.max(1, parseInt(qtyInput.value) || 1));
  qtyInput.value = qty;
  const product = previewSiCurrentProduct();
  const defaultSize = (product && product.sizes[0]) || '';
  while (previewSiUnits.length < qty) previewSiUnits.push({ size: defaultSize, performerId: PREVIEW_PERFORMERS[0].id, recipientName: '' });
  while (previewSiUnits.length > qty) previewSiUnits.pop();
  previewRenderUnitRows();
}
function previewUnitFieldChanged(index, field, value) {
  if (!previewSiUnits[index]) return;
  previewSiUnits[index][field] = value;
  if (field === 'performerId' && value) previewSiUnits[index].recipientName = '';
  previewRenderUnitRows();
}
function previewQuickAddCategory(category) {
  const sale = STORE_SALES.find(s => s.id === previewSaleId);
  const product = sale && sale.products.find(p => p.requiredCategory === category && p.active !== false);
  const sel = document.getElementById('previewProduct');
  if (!product || !sel) return;
  sel.value = product.id;
  previewProductChanged();
  sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
function previewCartAddFromForm() {
  const productId = document.getElementById('previewProduct').value;
  if (!productId) { toast('Choose an item'); return; }
  if (!previewSiUnits.length) { toast('Add at least one item'); return; }
  for (const u of previewSiUnits) {
    if (!u.size) { toast('Choose a size for every item'); return; }
    if (!u.performerId && !u.recipientName.trim()) { toast('Enter who each item is for'); return; }
  }
  previewSiUnits.forEach(u => {
    previewCart.push({ productId, size: u.size, quantity: 1, performerId: u.performerId || null, recipientName: u.performerId ? null : u.recipientName.trim() });
  });
  previewSiUnits = [];
  refresh();
  toast('Added to cart (preview only)');
}
function previewCartRemove(index) { previewCart.splice(index, 1); refresh(); }
function previewCartTotal() {
  const sale = STORE_SALES.find(s => s.id === previewSaleId);
  if (!sale) return 0;
  return previewCart.reduce((sum, line) => {
    const product = sale.products.find(p => p.id === line.productId);
    return sum + (product ? product.price * line.quantity : 0);
  }, 0);
}
function previewAttest(performerId, category) {
  previewAttestedKeys[performerId + ':' + category] = true;
  refresh();
  toast('Marked as already owned (preview only)');
}
function previewUnattest(performerId, category) {
  delete previewAttestedKeys[performerId + ':' + category];
  refresh();
  toast('Undone (preview only)');
}
function previewCheckoutConfirm() {
  if (!previewCart.length) return;
  const total = previewCartTotal();
  openModal(`
    <h3>Confirm order</h3>
    <p class="card-intro">In the real store, this would place the order and charge <b style="color:var(--cream)">${fmt$(total)}</b> to the family's card on file.</p>
    <div style="background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.3);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--gold-light);margin-bottom:16px">
      Preview mode — nothing will actually be charged, saved, or emailed.
    </div>
    <div style="display:flex;gap:10px">
      <button class="btn" id="previewCheckoutBtn" style="flex:1">Confirm (preview only)</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('previewCheckoutBtn').addEventListener('click', previewCheckoutComplete);
}
function previewCheckoutComplete() {
  closeModal();
  toast('Preview complete — nothing was saved or charged.');
}
VIEWS['a-store-preview'] = () => {
  const sale = STORE_SALES.find(x => x.id === previewSaleId);
  if (!sale) {
    return `<div class="page-head"><span class="eyebrow check-row" onclick="route('a-store-catalog')">‹ Merch Store</span></div>
      <p class="empty">Sale not found.</p>`;
  }
  const activeProducts = sale.products.filter(p => p.active !== false);
  const requiredCats = [...new Set(activeProducts.map(p => p.requiredCategory).filter(Boolean))];
  const sampleFemale = PREVIEW_PERFORMERS.find(p => p.gender === 'Female');

  const requiredBlock = requiredCats.length ? `
    <div class="card" style="margin-bottom:16px">
      <div class="card-hd">Still needed for the show</div>
      <div style="padding:10px 0">
        <div style="font-size:13px;color:var(--cream);margin-bottom:8px">${esc(sampleFemale.name)} <span style="color:var(--muted);font-size:11px">(this section only shows for female performers — boys never see it)</span></div>
        <div style="display:flex;flex-direction:column;gap:8px">
          ${requiredCats.map(cat => {
            const key = sampleFemale.id + ':' + cat;
            const ordered = previewCart.some(line => {
              const product = sale.products.find(p => p.id === line.productId);
              return line.performerId === sampleFemale.id && product && product.requiredCategory === cat;
            });
            const attested = !!previewAttestedKeys[key];
            const label = REQUIRED_CATEGORY_LABELS[cat] || cat;
            if (ordered) {
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
                <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
                <span class="pill pill-paid">Ordered</span>
              </div>`;
            }
            if (attested) {
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
                <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
                <span style="display:flex;align-items:center;gap:8px">
                  <span class="pill pill-amber">Already owns</span>
                  <button class="btn-ghost btn-sm" data-action="preview-unattest" data-performer="${sampleFemale.id}" data-category="${cat}">Undo</button>
                </span>
              </div>`;
            }
            return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
              <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
              <span style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                <span class="pill pill-due">Needed</span>
                <button class="btn-ghost btn-sm" data-action="preview-quick-add" data-category="${cat}">Order it</button>
                <button class="btn-ghost btn-sm" data-action="preview-attest" data-performer="${sampleFemale.id}" data-category="${cat}">Already have one</button>
              </span>
            </div>`;
          }).join('')}
        </div>
      </div>
    </div>` : '';

  if (!previewSiUnits.length && activeProducts.length) {
    previewSiUnits = [{ size: activeProducts[0].sizes[0] || '', performerId: PREVIEW_PERFORMERS[0].id, recipientName: '' }];
  }
  const addItemPanel = activeProducts.length ? `
    <div class="card">
      <div class="card-hd">Add an item</div>
      <div class="field"><label>Item</label>
        <select id="previewProduct" data-action="preview-product-changed">
          ${activeProducts.map(p => `<option value="${p.id}">${esc(p.name)} — ${fmt$(p.price)}</option>`).join('')}
        </select>
      </div>
      <img id="previewPhoto" src="${esc(activeProducts[0].photoUrl || '')}" style="display:${activeProducts[0].photoUrl ? '' : 'none'};max-width:100%;max-height:220px;border-radius:8px;margin-bottom:14px;object-fit:contain;background:rgba(255,255,255,0.03);cursor:zoom-in" onerror="this.style.display='none'" data-action="preview-photo-zoom" title="Click to enlarge">
      <div class="field"><label>Quantity</label><input type="number" id="previewQty" min="1" max="20" value="${previewSiUnits.length}" data-action="preview-qty-changed"></div>
      <div id="previewUnitRows">${previewUnitRowsHtml(activeProducts[0])}</div>
      <button class="btn" data-action="preview-cart-add">Add to cart</button>
    </div>` : `<div class="card"><p class="empty">No items available in this sale right now.</p></div>`;

  const total = previewCartTotal();
  const cartSummary = `
    <div class="card" style="position:sticky;top:24px">
      <div class="card-hd">Your cart</div>
      ${previewCart.length ? `<div style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px">
        ${previewCart.map((line, i) => {
          const product = sale.products.find(p => p.id === line.productId);
          const recipient = line.performerId ? ((PREVIEW_PERFORMERS.find(k => k.id === line.performerId) || {}).name || 'Performer') : line.recipientName;
          return `<div style="display:flex;justify-content:space-between;gap:10px;font-size:13px">
            <div>
              <div style="color:var(--cream)">${esc(product ? product.name : '—')} (${esc(line.size)}) x${line.quantity}</div>
              <div style="font-size:11px;color:var(--muted)">${esc(recipient)}</div>
            </div>
            <div style="display:flex;align-items:center;gap:8px;flex-shrink:0">
              <span style="color:var(--gold-light)">${fmt$(product ? product.price * line.quantity : 0)}</span>
              <button class="icon-btn" title="Remove" aria-label="Remove" data-action="preview-cart-remove" data-index="${i}">&times;</button>
            </div>
          </div>`;
        }).join('')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border-soft);margin:0 0 14px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <span style="font-size:14px;color:var(--cream)">Total due today</span>
        <span style="font-family:'Libre Baskerville',serif;font-size:26px;font-weight:700;color:var(--cream)">${fmt$(total)}</span>
      </div>
      <div style="font-size:12px;color:var(--muted);margin-bottom:14px">Preview mode — no real card is charged</div>
      <button class="btn" style="width:100%" data-action="preview-checkout-confirm">Place order — charge ${fmt$(total)}</button>`
      : `<p class="summary-empty" style="text-align:center;padding:12px 0;font-style:italic;color:var(--muted)">Add items to get started.</p>`}
    </div>`;

  return `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('a-store-catalog')">‹ Merch Store</span>
      <h2 class="serif">Preview — <em>${esc(sale.name)}</em></h2>
      <p class="sub">Orders close ${fmtLocalDT(sale.deadlineAt)}. Charged once to a card on file, separate from tuition.</p>
    </div>
    <div style="background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.3);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--gold-light);margin-bottom:16px">
      This is what an eligible family sees on the Store tab. It's fully interactive using sample stand-in performers — nothing here touches a real family, and no real charge happens even after Confirm.
    </div>
    ${requiredBlock}
    <div class="grid-2" style="align-items:start">
      <div>${addItemPanel}</div>
      <div>${cartSummary}</div>
    </div>`;
};
VIEW_INIT['a-store-preview'] = () => {
  document.querySelectorAll('[data-action="preview-unattest"]').forEach(btn => {
    btn.addEventListener('click', () => previewUnattest(btn.dataset.performer, btn.dataset.category));
  });
  document.querySelectorAll('[data-action="preview-quick-add"]').forEach(btn => {
    btn.addEventListener('click', () => previewQuickAddCategory(btn.dataset.category));
  });
  document.querySelectorAll('[data-action="preview-attest"]').forEach(btn => {
    btn.addEventListener('click', () => previewAttest(btn.dataset.performer, btn.dataset.category));
  });
  const productEl = document.getElementById('previewProduct');
  if (productEl) productEl.addEventListener('change', previewProductChanged);
  const photoEl = document.getElementById('previewPhoto');
  if (photoEl) photoEl.addEventListener('click', previewPhotoZoom);
  const qtyEl = document.getElementById('previewQty');
  if (qtyEl) qtyEl.addEventListener('change', previewQtyChanged);
  wireStorePreviewUnitRows();
  document.querySelectorAll('[data-action="preview-cart-add"]').forEach(btn => {
    btn.addEventListener('click', previewCartAddFromForm);
  });
  document.querySelectorAll('[data-action="preview-cart-remove"]').forEach(btn => {
    btn.addEventListener('click', () => previewCartRemove(parseInt(btn.dataset.index)));
  });
  document.querySelectorAll('[data-action="preview-checkout-confirm"]').forEach(btn => {
    btn.addEventListener('click', previewCheckoutConfirm);
  });
};
async function toggleSaleActive(saleId) {
  const s = STORE_SALES.find(x => x.id === saleId);
  if (!s) return;
  const newActive = s.active === false;
  try {
    const updated = await apiFetch('/store/sales/' + saleId, { method: 'PUT', body: JSON.stringify({ active: newActive }) });
    const idx = STORE_SALES.findIndex(x => x.id === saleId);
    if (idx >= 0) STORE_SALES[idx] = updated;
    const listEl = document.getElementById('storeCatalogList');
    if (listEl) listEl.innerHTML = renderStoreCatalogList();
    const tabsEl = document.getElementById('storeCatalogTabs');
    if (tabsEl) tabsEl.outerHTML = renderStoreCatalogTabs();
    wireStoreCatalogGroup();
    toast(newActive ? s.name + ' restored to active' : s.name + ' archived');
  } catch (e) { toastError(e.message); }
}
// Separate from Active/Archived: lets Lori stage a sale (add products, sanity
// check via Preview) without parents seeing it, then publish it explicitly
// when ready. New sales always start unpublished (see StoreRoutes::createSale).
async function toggleSaleVisibility(saleId) {
  const s = STORE_SALES.find(x => x.id === saleId);
  if (!s) return;
  const newVisible = s.visibleToParents === false;
  try {
    const updated = await apiFetch('/store/sales/' + saleId, { method: 'PUT', body: JSON.stringify({ visibleToParents: newVisible }) });
    const idx = STORE_SALES.findIndex(x => x.id === saleId);
    if (idx >= 0) STORE_SALES[idx] = updated;
    const listEl = document.getElementById('storeCatalogList');
    if (listEl) listEl.innerHTML = renderStoreCatalogList();
    wireStoreCatalogGroup();
    toast(newVisible ? s.name + ' published to parents' : s.name + ' hidden from parents');
  } catch (e) { toastError(e.message); }
}
async function toggleProductActive(saleId, productId) {
  const s = STORE_SALES.find(x => x.id === saleId);
  const p = s && s.products.find(x => x.id === productId);
  if (!p) return;
  const newActive = p.active === false;
  try {
    const updated = await apiFetch('/store/products/' + productId, { method: 'PUT', body: JSON.stringify({ active: newActive }) });
    const pIdx = s.products.findIndex(x => x.id === productId);
    if (pIdx >= 0) s.products[pIdx] = updated;
    const listEl = document.getElementById('storeCatalogList');
    if (listEl) listEl.innerHTML = renderStoreCatalogList();
    wireStoreCatalogGroup();
    toast(newActive ? p.name + ' restored' : p.name + ' retired');
  } catch (e) { toastError(e.message); }
}
// Year-to-year: copy the sale shell into a fresh draft (deadline blanked, forcing
// re-entry), then re-create each of the old sale's products under the new one —
// same "conscious re-entry" pattern as duplicateCatalogItem().
function duplicateSale(saleId) {
  const s = STORE_SALES.find(x => x.id === saleId);
  if (!s) return;
  scDuplicateSourceProducts = s.products;
  saleModal(null, { name: s.name, seasonYear: s.seasonYear + 1, deadlineAt: '' });
}
function saleModal(saleId, prefill) {
  const s = saleId ? STORE_SALES.find(x => x.id === saleId) : (prefill || null);
  openModal(`
    <h3>${saleId ? 'Edit sale' : 'New sale'}</h3>
    <div class="field"><label>Sale name</label><input type="text" id="smName" value="${s ? esc(s.name) : ''}" placeholder="e.g. Fall 2026 PC T-Shirts"></div>
    <div class="field-row">
      <div class="field"><label>Season year</label><input type="number" id="smSeasonYear" min="2024" max="2099" value="${s && s.seasonYear ? s.seasonYear : ''}"></div>
      <div class="field"><label>Order deadline</label><input type="datetime-local" id="smDeadline" value="${s && s.deadlineAt && saleId ? s.deadlineAt.replace(' ', 'T').slice(0, 16) : ''}"></div>
    </div>
    ${saleId ? `<div class="field" style="flex-direction:row;align-items:center;gap:10px;margin-top:4px">
      <input type="checkbox" id="smActive" style="width:auto" ${s.active !== false ? 'checked' : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">Active — not archived</label>
    </div>
    <p style="font-size:12px;color:var(--muted);margin-top:2px">Whether families can actually see this sale is controlled separately by Publish/Unpublish on the Catalog list.</p>` : ''}
    <button class="btn" id="saleSaveBtn" style="margin-top:10px">${saleId ? 'Save changes' : 'Create sale'}</button>`);
  document.getElementById('saleSaveBtn').addEventListener('click', () => saveSale(saleId || ''));
}
async function saveSale(saleId) {
  const name = document.getElementById('smName').value.trim();
  const seasonYear = parseInt(document.getElementById('smSeasonYear').value) || 0;
  const deadlineAt = document.getElementById('smDeadline').value;
  if (!name) { toast('Sale name is required'); return; }
  if (!seasonYear) { toast('Season year is required'); return; }
  if (!deadlineAt) { toast('An order deadline is required'); return; }
  const payload = { name, seasonYear, deadlineAt };
  try {
    if (saleId) {
      const activeEl = document.getElementById('smActive');
      payload.active = activeEl ? activeEl.checked : true;
      const updated = await apiFetch('/store/sales/' + saleId, { method: 'PUT', body: JSON.stringify(payload) });
      const idx = STORE_SALES.findIndex(x => x.id === saleId);
      if (idx >= 0) STORE_SALES[idx] = updated;
    } else {
      const created = await apiFetch('/store/sales', { method: 'POST', body: JSON.stringify(payload) });
      if (scDuplicateSourceProducts) {
        for (const p of scDuplicateSourceProducts) {
          const newProduct = await apiFetch('/store/sales/' + created.id + '/products', {
            method: 'POST',
            body: JSON.stringify({ name: p.name, requiredCategory: p.requiredCategory, price: p.price, sizes: p.sizes, photoUrl: p.photoUrl, sortOrder: p.sortOrder })
          });
          created.products.push(newProduct);
        }
        scDuplicateSourceProducts = null;
      }
      STORE_SALES.push(created);
    }
    closeModal();
    if (currentView === 'a-store-catalog') {
      const listEl = document.getElementById('storeCatalogList');
      if (listEl) listEl.innerHTML = renderStoreCatalogList();
      const tabsEl = document.getElementById('storeCatalogTabs');
      if (tabsEl) tabsEl.outerHTML = renderStoreCatalogTabs();
      wireStoreCatalogGroup();
    } else refresh();
    toast(saleId ? 'Sale updated' : name + ' created');
  } catch (e) { toastError(e.message); }
}
const STANDARD_TSHIRT_SIZES = ['Youth M', 'Youth L', 'Youth XL', 'Adult S', 'Adult M', 'Adult L', 'Adult XL', 'Adult 2XL', 'Adult 3XL'];
let pmSizesList = []; // chip-input state for the product modal's Sizes field

function pmRenderSizeChips() {
  if (!pmSizesList.length) return '<p style="font-size:12px;color:var(--muted);font-style:italic;margin:0">No sizes added yet.</p>';
  return pmSizesList.map((s, i) => `
    <span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;padding:4px 6px 4px 12px;border-radius:20px;border:1px solid var(--border-soft);color:var(--cream);background:rgba(255,255,255,0.03)">
      ${esc(s)}
      <button type="button" title="Remove" data-action="pm-remove-size" data-index="${i}" style="width:16px;height:16px;border-radius:50%;border:none;background:rgba(255,255,255,0.08);color:var(--muted);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:11px;line-height:1;padding:0">&times;</button>
    </span>`).join('');
}
function pmSyncSizeChips() {
  const el = document.getElementById('pmSizesChips');
  if (el) el.innerHTML = pmRenderSizeChips();
  wirePmSizeChips();
}
function wirePmSizeChips() {
  const el = document.getElementById('pmSizesChips');
  if (!el) return;
  el.querySelectorAll('[data-action="pm-remove-size"]').forEach(btn => {
    btn.addEventListener('click', () => pmRemoveSize(parseInt(btn.dataset.index)));
  });
}
function pmAddSize() {
  const input = document.getElementById('pmNewSize');
  const val = input.value.trim();
  if (!val) return;
  if (!pmSizesList.includes(val)) pmSizesList.push(val);
  input.value = '';
  pmSyncSizeChips();
  input.focus();
}
function pmAddSizeOnEnter(event) {
  if (event.key === 'Enter') { event.preventDefault(); pmAddSize(); }
}
function pmRemoveSize(index) {
  pmSizesList.splice(index, 1);
  pmSyncSizeChips();
}
function pmAddPresetSizes() {
  STANDARD_TSHIRT_SIZES.forEach(s => { if (!pmSizesList.includes(s)) pmSizesList.push(s); });
  pmSyncSizeChips();
}
function productModal(saleId, productId) {
  const sale = STORE_SALES.find(x => x.id === saleId);
  const p = productId && sale ? sale.products.find(x => x.id === productId) : null;
  pmSizesList = p ? [...p.sizes] : [];
  openModal(`
    <h3>${productId ? 'Edit product' : 'New product'}</h3>
    <div class="field"><label>Product name</label><input type="text" id="pmName" value="${p ? esc(p.name) : ''}" placeholder="e.g. PC T-Shirt"></div>
    <div class="field"><label>Price</label>
      <div class="money"><span class="cur" aria-hidden="true">$</span><input type="text" inputmode="decimal" id="pmPrice" value="${moneyValue(p ? p.price : '')}" placeholder="e.g. 20" aria-label="Price in dollars"></div></div>
    <div class="field" style="flex-direction:row;align-items:center;gap:10px">
      <input type="checkbox" id="pmIsRequired" style="width:auto" ${p && p.requiredCategory ? 'checked' : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">This is a required show item <span style="color:var(--muted)">(tracked per performer, females only)</span></label>
    </div>
    <div class="field" id="pmRequiredCategoryWrap" style="display:${p && p.requiredCategory ? '' : 'none'}">
      <label>Which one?</label>
      <select id="pmRequiredCategory">
        <option value="boy_shorts" ${p && p.requiredCategory === 'boy_shorts' ? 'selected' : ''}>Boy shorts</option>
        <option value="tights" ${p && p.requiredCategory === 'tights' ? 'selected' : ''}>Tights</option>
        <option value="leggings" ${p && p.requiredCategory === 'leggings' ? 'selected' : ''}>Leggings</option>
      </select>
    </div>
    <div class="field"><label>Sizes</label>
      <div style="display:flex;gap:8px;margin-bottom:8px">
        <input type="text" id="pmNewSize" placeholder="e.g. Youth M">
        <button type="button" class="btn-ghost btn-sm" id="pmAddSizeBtn" title="Add size" aria-label="Add size">+</button>
      </div>
      <div id="pmSizesChips" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px">${pmRenderSizeChips()}</div>
      <button type="button" class="btn-ghost btn-sm" id="pmAddPresetBtn">+ Standard t-shirt sizes</button>
    </div>
    <div class="field"><label>Photo URL <span class="plain-cap">(optional — most products don't need one)</span></label>
      <input type="url" id="pmPhotoUrl" value="${p && p.photoUrl ? esc(p.photoUrl) : ''}" placeholder="https://...">
      <img id="pmPhotoPreview" src="${p && p.photoUrl ? esc(p.photoUrl) : ''}" style="display:${p && p.photoUrl ? '' : 'none'};max-width:160px;max-height:160px;border-radius:8px;margin-top:8px;object-fit:contain;background:rgba(255,255,255,0.03)" onerror="this.style.display='none'" onload="this.style.display=''">
    </div>
    ${productId ? `<div class="field" style="flex-direction:row;align-items:center;gap:10px;margin-top:4px">
      <input type="checkbox" id="pmActive" style="width:auto" ${p.active !== false ? 'checked' : ''}>
      <label style="text-transform:none;letter-spacing:0;font-size:13px;color:var(--cream)">Active — available to buy</label>
    </div>` : ''}
    <button class="btn" id="productSaveBtn" style="margin-top:10px">${productId ? 'Save changes' : 'Add product'}</button>`);
  document.getElementById('pmIsRequired').addEventListener('change', pmRequiredToggled);
  document.getElementById('pmNewSize').addEventListener('keydown', pmAddSizeOnEnter);
  document.getElementById('pmAddSizeBtn').addEventListener('click', pmAddSize);
  document.getElementById('pmAddPresetBtn').addEventListener('click', pmAddPresetSizes);
  document.getElementById('pmPhotoUrl').addEventListener('input', pmPhotoUrlChanged);
  document.getElementById('productSaveBtn').addEventListener('click', () => saveProduct(saleId, productId || ''));
  wirePmSizeChips();
}
function pmRequiredToggled() {
  const wrap = document.getElementById('pmRequiredCategoryWrap');
  if (wrap) wrap.style.display = document.getElementById('pmIsRequired').checked ? '' : 'none';
}
function pmPhotoUrlChanged() {
  const url = document.getElementById('pmPhotoUrl').value.trim();
  const img = document.getElementById('pmPhotoPreview');
  if (!img) return;
  if (url) { img.src = url; } else { img.style.display = 'none'; }
}
async function saveProduct(saleId, productId) {
  const name = document.getElementById('pmName').value.trim();
  const price = readMoney(document.getElementById('pmPrice'));
  if (price === null) { toastError('Price must be a dollar amount, like 20 or 20.00'); return; }
  const isRequired = document.getElementById('pmIsRequired').checked;
  const requiredCategory = isRequired ? document.getElementById('pmRequiredCategory').value : null;
  const photoUrl = document.getElementById('pmPhotoUrl').value.trim();
  const sizes = pmSizesList.slice();
  if (!name) { toast('Product name is required'); return; }
  if (!sizes.length) { toast('At least one size is required'); return; }
  const payload = { name, price, requiredCategory, sizes, photoUrl };
  try {
    const sale = STORE_SALES.find(x => x.id === saleId);
    if (productId) {
      const activeEl = document.getElementById('pmActive');
      payload.active = activeEl ? activeEl.checked : true;
      const updated = await apiFetch('/store/products/' + productId, { method: 'PUT', body: JSON.stringify(payload) });
      const idx = sale.products.findIndex(x => x.id === productId);
      if (idx >= 0) sale.products[idx] = updated;
    } else {
      const created = await apiFetch('/store/sales/' + saleId + '/products', { method: 'POST', body: JSON.stringify(payload) });
      sale.products.push(created);
    }
    closeModal();
    scExpandedSaleId = saleId;
    if (currentView === 'a-store-catalog') {
      const listEl = document.getElementById('storeCatalogList');
      if (listEl) listEl.innerHTML = renderStoreCatalogList();
      wireStoreCatalogGroup();
    } else refresh();
    toast(productId ? 'Product updated' : name + ' added');
  } catch (e) { toastError(e.message); }
}
VIEWS['a-store-catalog'] = () => `
  <div class="page-head">
    <h2 class="serif">Merch <em>Store</em></h2>
  </div>
  <div class="card">
    <div class="card-hd">Sales <button class="btn-ghost btn-sm" data-action="sc-add-sale">+ Add sale</button></div>
    ${renderStoreCatalogTabs()}
    <div id="storeCatalogList">${renderStoreCatalogList()}</div>
  </div>`;
VIEW_INIT['a-store-catalog'] = () => {
  document.querySelectorAll('[data-action="sc-add-sale"]').forEach(btn => {
    btn.addEventListener('click', () => saleModal());
  });
  wireStoreCatalogGroup();
};
